/**
 * SubscribePage.js
 * Shows three subscription option cards:
 *   1. Yearly Subscription   → navigates to /my-account/magazine/yearly-checkout
 *   2. Recent Magazine Order → navigates to /my-account/magazine/order-checkout
 *   3. Sample Magazine       → embedded PDF viewer
 * Below cards: magazine payment history table.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, ShoppingCart, FileText, ChevronRight, CheckCircle, ArrowRight, Loader, LifeBuoy } from 'lucide-react';
import { vendorService } from '../../services/api';
import { useToast } from '../../hooks/useToast';

const SubscribePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await vendorService.getMagazineConfig();
      if (res.success && res.data) setConfig(res.data);
    } catch { /* silent */ }
    finally { setConfigLoading(false); }
  };

  const yearlyPrice = config?.yearly_price ?? 699;
  const copyPrice   = config?.copy_price ?? 399;
  const edition     = config ? `${config.recent_month} ${config.recent_year}` : 'Loading...';
  const samplePdfUrl = config?.sample_pdf_url;

  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    return `${base}${url}`;
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-gray-800 text-left font-sans">TII Magazine</h1>
        <p className="text-gray-500 text-sm mt-1 text-left font-medium">Choose a subscription option below to stay updated with design trends.</p>
      </div>

      {/* ── Option Cards ──────────────────────────────────────────── */}
      {configLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader size={28} className="animate-spin text-[#2ab391]" />
        </div>
      ) : (
        <div className="space-y-6">
          
          {/* Card 1 — Yearly Subscription */}
          <div
            onClick={() => navigate('/my-account/magazine/yearly-checkout')}
            className="group cursor-pointer bg-white border border-gray-200 border-t-4 border-t-[#2ab391] lg:border-l-4 lg:border-l-[#2ab391] lg:border-t-0 rounded-2xl p-6 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col lg:flex-row lg:items-center justify-between gap-6"
          >
            {/* Left Section: Icon and Title */}
            <div className="flex items-start gap-4 lg:w-[35%]">
              <div className="w-12 h-12 bg-[#e0f7f8] rounded-xl flex items-center justify-center flex-shrink-0">
                <BookOpen size={24} className="text-[#2ab391]" />
              </div>
              <div className="text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-bold text-gray-800">Yearly Subscription</h3>
                  <span className="bg-[#2ab391] text-white text-[9px] font-extrabold px-2 py-0.5 rounded-md uppercase tracking-wider">
                    Popular
                  </span>
                </div>
                <h5 className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest mt-1">
                  Early Subscriber Offer
                </h5>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  4 Print issues delivered straight to your door.
                </p>
              </div>
            </div>

            {/* Middle-Left Section: Price */}
            <div className="text-left lg:w-[22%] border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-6">
              <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">
                Subscription Fee
              </p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-xl font-black text-gray-850">₹{yearlyPrice}</span>
                <span className="text-xs text-gray-400 font-bold">/ year</span>
                <span className="text-xs text-gray-350 line-through ml-1.5 font-medium">₹999</span>
              </div>
              <p className="text-[10px] text-emerald-600 font-bold mt-1">
                You save 30% instantly
              </p>
            </div>

            {/* Middle-Right Section: Bullet Points */}
            <div className="text-left lg:w-[25%] border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-6">
              <ul className="space-y-1.5">
                {[
                  '4 Print Editions per Year',
                  'GST & Delivery Included',
                  'Instant dispatch tracking'
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <CheckCircle size={13} className="text-[#2ab391] flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Section: Action Button */}
            <div className="lg:w-[18%] flex justify-end">
              <button className="w-full lg:w-[165px] h-[42px] bg-[#2ab391] hover:bg-[#208c71] text-white text-xs font-bold rounded-xl shadow-xs flex items-center justify-center gap-2 transition-all cursor-pointer">
                <span className="whitespace-nowrap">Subscribe Now</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Card 2 — Recent Magazine Order */}
          <div
            onClick={() => navigate('/my-account/magazine/order-checkout')}
            className="group cursor-pointer bg-white border border-gray-200 border-t-4 border-t-indigo-500 lg:border-l-4 lg:border-l-indigo-500 lg:border-t-0 rounded-2xl p-6 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col lg:flex-row lg:items-center justify-between gap-6"
          >
            {/* Left Section: Icon and Title */}
            <div className="flex items-start gap-4 lg:w-[35%]">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <ShoppingCart size={24} className="text-indigo-500" />
              </div>
              <div className="text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-bold text-gray-800">Recent Magazine Order</h3>
                  <span className="bg-indigo-50 text-indigo-650 text-[9px] font-extrabold px-2 py-0.5 rounded-md uppercase tracking-wider">
                    Single Issue
                  </span>
                </div>
                <h5 className="text-[10px] font-extrabold text-indigo-500 uppercase tracking-widest mt-1">
                  Edition: {edition}
                </h5>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Buy physical print copies of current issue.
                </p>
              </div>
            </div>

            {/* Middle-Left Section: Price */}
            <div className="text-left lg:w-[22%] border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-6">
              <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">
                Price Per Copy
              </p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-xl font-black text-gray-850">₹{copyPrice}</span>
                <span className="text-xs text-gray-400 font-bold">/ copy</span>
              </div>
              <p className="text-[10px] text-indigo-600 font-bold mt-1">
                Premium print quality
              </p>
            </div>

            {/* Middle-Right Section: Bullet Points */}
            <div className="text-left lg:w-[25%] border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-6">
              <ul className="space-y-1.5">
                {[
                  'Physical Copy Delivered',
                  'Architect & Designer Features',
                  'Industry insights and trends'
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <CheckCircle size={13} className="text-indigo-400 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Section: Action Button */}
            <div className="lg:w-[18%] flex justify-end">
              <button className="w-full lg:w-[165px] h-[42px] bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs flex items-center justify-center gap-2 transition-all cursor-pointer">
                <span className="whitespace-nowrap">Order Now</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Card 3 — Sample Magazine */}
          <div className="group bg-white border border-gray-200 border-t-4 border-t-amber-500 lg:border-l-4 lg:border-l-amber-500 lg:border-t-0 rounded-2xl p-6 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            {/* Left Section: Icon and Title */}
            <div className="flex items-start gap-4 lg:w-[50%] text-left">
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText size={24} className="text-amber-500" />
              </div>
              <div className="text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-bold text-gray-800">Sample Magazine</h3>
                  <span className="bg-amber-100 text-amber-700 text-[9px] font-extrabold px-2 py-0.5 rounded-md uppercase tracking-wider">
                    Free Preview
                  </span>
                </div>
                <h5 className="text-[10px] font-extrabold text-amber-500 uppercase tracking-widest mt-1">
                  Digital Edition
                </h5>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Download a free sample issue of TII Magazine to preview our architecture and local designer spotlights before subscribing.
                </p>
              </div>
            </div>

            {/* Middle Section: Bullet Points */}
            <div className="text-left lg:w-[32%] border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-6">
              <ul className="space-y-1.5">
                {[
                  'Architect & designer features',
                  'Project showcase previews',
                  '100% Free digital download'
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <CheckCircle size={13} className="text-amber-500 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Section: Action Button */}
            <div className="lg:w-[18%] flex flex-col items-end gap-1.5 justify-center">
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  if (!samplePdfUrl) {
                    toast.info('Sample magazine PDF is currently not available. Please try again after some time.');
                    return;
                  }
                  try {
                    const response = await fetch(getMediaUrl(samplePdfUrl));
                    if (!response.ok) throw new Error('File not found');
                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = 'TII_Magazine_Sample.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(blobUrl);
                  } catch (err) {
                    console.error('Fetch download failed:', err);
                    toast.error('Sample PDF is currently unavailable for download.');
                  }
                }}
                className={`w-full lg:w-[165px] h-[42px] text-xs font-bold rounded-xl shadow-xs flex items-center justify-center gap-2 transition-all cursor-pointer ${
                  samplePdfUrl
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-500'
                }`}
              >
                <span className="whitespace-nowrap">Download PDF</span>
                <ArrowRight size={14} />
              </button>
              {!samplePdfUrl && (
                <span className="text-[10px] text-gray-400 font-semibold text-right">
                  Currently not available. Will be uploaded soon.
                </span>
              )}
            </div>
          </div>

          {/* Help & Support Desk Link */}
          <div className="flex flex-col sm:flex-row items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-4 gap-3 shadow-xs">
            <div className="flex items-center gap-3 text-left">
              <div className="bg-[#e0f7f8] text-[#2ab391] p-2 rounded-lg flex-shrink-0">
                <LifeBuoy size={20} />
              </div>
              <div>
                <h4 className="font-bold text-sm text-gray-800">Need help with payments or plans?</h4>
                <p className="text-xs text-gray-500 mt-0.5">Create a support ticket, and our team will assist you shortly.</p>
              </div>
            </div>
            <Link
              to="/my-account/support-tickets"
              className="bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 text-xs font-bold px-4 py-2 rounded-xl transition-all shadow-xs shrink-0 cursor-pointer"
            >
              Go to Support Desk
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribePage;
